// src/pages/Game/GameManualResult.jsx
import React, { useState, useEffect } from "react";
import api from "../../services/api";
import { toast } from "react-toastify";

// ── UTC → IST helper ──────────────────────────────────────────────────────────
const toIST = (utcValue) => {
  if (!utcValue) return "--";
  const date = new Date(utcValue);
  if (isNaN(date)) return "--";
  return date.toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "₹0.00";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// ── Per-game state factory ────────────────────────────────────────────────────
const makeGameState = () => ({
  selectedRound: null,
  betData: [],
  loading: false,
});

const GameManualResult = () => {
  const [rounds, setRounds] = useState({
    classic: [],
    roulette: [],
    funtarget: [],
  });
  const [gameState, setGameState] = useState({
    classic: makeGameState(),
    roulette: makeGameState(),
    funtarget: makeGameState(),
  });

  // 🔒 PAGE-OPEN PASSWORD GATE
  const [auth, setAuth] = useState({
    ok: false,
    input: "",
    error: "",
    showPassword: false,
  });

  const handleAuthSubmit = () => {
    if (auth.input !== "Ravan@07#") {
      setAuth((prev) => ({ ...prev, error: "Incorrect password. Try again." }));
      return;
    }
    setAuth((prev) => ({ ...prev, ok: true, error: "" }));
  };

  // ── helpers to read/write per-game state ──────────────────────────────────
  const gs = (game) => gameState[game];
  const setGs = (game, patch) =>
    setGameState((prev) => ({ ...prev, [game]: { ...prev[game], ...patch } }));

  // ── fetch upcoming rounds ─────────────────────────────────────────────────
  const fetchUpcomingRounds = async () => {
    try {
      const res = await api.get("/admin/game/upcoming-rounds");
      if (res.data?.success) {
        const r = res.data.rounds;
        // Convert endTime → IST for display
        const fmt = (list) =>
          (list || []).map((round) => ({
            ...round,
            timeFormatted: toIST(round.endTime || round.startTime),
          }));
        setRounds({
          classic: fmt(r.classic),
          roulette: fmt(r.roulette),
          funtarget: fmt(r.funtarget),
        });
      }
    } catch (err) {
      console.error("Failed to fetch upcoming rounds", err);
    }
  };

  useEffect(() => {
    if (!auth.ok) return; // 🔒 jab tak verify nahi, fetch mat karo
    fetchUpcomingRounds();
    const interval = setInterval(fetchUpcomingRounds, 10000);
    return () => clearInterval(interval);
  }, [auth.ok]);

  // ── auto-refresh bets for each selected round ────────────────────────────
  useEffect(() => {
    if (!auth.ok) return;
    const timers = ["classic", "roulette", "funtarget"].map((game) => {
      const selected = gs(game).selectedRound;
      if (!selected) return null;
      return setInterval(async () => {
        try {
          const res = await api.get(`/admin/game/round-bets/${selected.id}`, {
            params: { gameType: game },
          });
          setGs(game, { betData: res.data.data || [] });
        } catch {
          console.error(`Auto bet refresh failed for ${game}`);
        }
      }, 3000);
    });
    return () => timers.forEach((t) => t && clearInterval(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth.ok,
    gameState.classic.selectedRound,
    gameState.roulette.selectedRound,
    gameState.funtarget.selectedRound,
  ]);

  // ── open a round (load bets) ──────────────────────────────────────────────
  const openRound = async (game, round) => {
    try {
      const res = await api.get(`/admin/game/round-bets/${round.id}`, {
        params: { gameType: game },
      });
      setGs(game, { selectedRound: round, betData: res.data.data || [] });
    } catch {
      toast.error("Failed to load bets");
    }
  };

  // ── declare result (no password — already verified on page open) ──────────
  const handleDeclareResult = async (game, payload) => {
    const selected = gs(game).selectedRound;
    if (!selected) return;

    setGs(game, { loading: true });

    try {
      const body = { gameType: game, roundIds: [selected.id], ...payload };
      const res = await api.post("/admin/game/manual-result", body);
      if (res.data.success) {
        toast.success("Result declared");
        setGs(game, { selectedRound: null, betData: [] });
        fetchUpcomingRounds();
      } else {
        toast.error(res.data.error || "Failed to declare result");
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to declare result");
    } finally {
      setGs(game, { loading: false });
    }
  };

  // ── clear manual result ───────────────────────────────────────────────────
  const handleClearManualResult = async (game, roundId, drawNumber) => {
    if (!window.confirm(`Clear manual result for Draw #${drawNumber}?`)) return;
    try {
      await api.delete(`/admin/game/manual-result/${roundId}`, {
        params: { gameType: game },
      });
      toast.success(`Cleared Draw #${drawNumber}`);
      fetchUpcomingRounds();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to clear");
    }
  };
 const handleMultiplierChange = async (game, round, newMultiplier) => {
    try {
      const res = await api.post("/admin/game/set-multiplier", {
        gameType: game,
        roundId: round.id,
        multiplier: newMultiplier,
      });
      if (res.data.success) {
        toast.success(`Draw #${round.drawNumber} → ${newMultiplier}X set`);
        fetchUpcomingRounds();
      } else {
        toast.error(res.data.error || "Failed to set multiplier");
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to set multiplier");
    }
  };

  const getCardImage = (card, suit) => `/img/cards/${card}-${suit}.png`;

  // ── shared table component ────────────────────────────────────────────────
  const RoundsTable = ({ game, title }) => (
    <div className="col-md-6">
      <div className="admin-card-one">
        <div className="admin-card-header">
          <h2 className="admin-card-heading">{title} Upcoming Rounds</h2>
        </div>
        <div className="admin-card-body">
          <div className="table-responsive">
            <table className="table table-admin-one">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Draw #</th>
                  <th>Time (IST)</th>
                  <th>Status</th>
                  <th>Manual Result</th>
                  <th>2X</th>
                  <th>3X</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rounds[game].length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted">
                      No upcoming rounds
                    </td>
                  </tr>
                ) : (
                  rounds[game].map((round) => (
                    <tr key={round.id}>
                      <td>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => openRound(game, round)}
                        >
                          Select
                        </button>
                      </td>
                      <td>#{round.drawNumber}</td>
                      <td>{round.timeFormatted}</td>
                      <td>
                        <span
                          className={`badge bg-${round.status === "active" ? "success" : "warning"}`}
                        >
                          {round.status}
                        </span>
                      </td>
                      <td>
                        {round.hasManualResult ? (
                          <span className="badge bg-info">
                            {game === "classic"
                              ? round.manualResult?.card
                              : game === "roulette"
                                ? round.manualResult?.number
                                : round.manualResult?.target}{" "}
                           
                          </span>
                        ) : (
                          "Auto"
                        )}
                      </td>
                      <td>
  <input
    className="form-check-input"
    type="checkbox"
    checked={round.multiplier === 2}
    onChange={() =>
      handleMultiplierChange(game, round, round.multiplier === 2 ? 1 : 2)
    }
  />
</td>
<td>
  <input
    className="form-check-input"
    type="checkbox"
    checked={round.multiplier === 3}
    onChange={() =>
      handleMultiplierChange(game, round, round.multiplier === 3 ? 1 : 3)
    }
  />
</td>
                      <td>
                        {round.hasManualResult && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() =>
                              handleClearManualResult(
                                game,
                                round.id,
                                round.drawNumber,
                              )
                            }
                          >
                            Clear
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Classic result panel (card grid) ─────────────────────────────────────
  const ClassicResultPanel = () => {
    const { selectedRound, betData, loading } = gs("classic");
    return (
      <div className="col-md-6">
        {selectedRound ? (
          <div className="admin-card-one">
            <div className="admin-card-header">
              <h2 className="admin-card-heading">
                Draw #{selectedRound.drawNumber} — Select Card
              </h2>
            </div>
            <div className="admin-card-body">
              <div className="total-points-section mb-4 p-3 bg-light rounded">
                <div className="row align-items-center">
                  <div className="col-md-8">
                    <h4 className="mb-0 text-black">
                      Total Bet Amount for this Draw:
                    </h4>
                  </div>
                  <div className="col-md-4 text-md-end">
                    <h3 className="mb-0 text-primary fw-bold">
                      {formatCurrency(
                        betData.reduce((s, b) => s + (b.totalAmount || 0), 0),
                      )}
                    </h3>
                  </div>
                </div>
              </div>
              <div className="card-row">
                {betData.map((b, i) => (
                  <div
                    key={i}
                    className="card-box"
                    style={{
                      cursor: loading ? "not-allowed" : "pointer",
                      opacity: loading ? 0.6 : 1,
                    }}
                    onClick={() =>
                      !loading &&
                      handleDeclareResult("classic", {
                        card: b.card,
                        suit: b.suit,
                      })
                    }
                  >
                    <img
                      src={getCardImage(b.card, b.suit)}
                      className="card-img"
                      alt={`${b.card}-${b.suit}`}
                    />
                    <input
                      type="text"
                      className="card-input"
                      value={formatCurrency(b.totalAmount)}
                      readOnly
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center mt-5 text-muted">
            Select a draw to view bets
          </div>
        )}
      </div>
    );
  };

  // ── Roulette / FunTarget result panel (number grid) ──────────────────────
  const NumberResultPanel = ({ game }) => {
    const { selectedRound, betData, loading } = gs(game);
    const maxNum = game === "roulette" ? 36 : 9;

    const betMap = {};
    betData.forEach((b) => {
      betMap[b.number ?? b.result] = b.totalAmount || 0;
    });

    // ── Build number list with "00" for roulette ──
    const numbers =
      game === "roulette"
        ? ["0", "00", ...Array.from({ length: 36 }, (_, i) => String(i + 1))]
        : Array.from({ length: maxNum + 1 }, (_, n) => String(n));

    return (
      <div className="col-md-6">
        {selectedRound ? (
          <div className="admin-card-one">
            <div className="admin-card-header">
              <h2 className="admin-card-heading">
                Draw #{selectedRound.drawNumber} — Select Number
              </h2>
            </div>
            <div className="admin-card-body">
              <div className="total-points-section mb-4 p-3 bg-light rounded">
                <div className="row align-items-center">
                  <div className="col-md-8">
                    <h4 className="mb-0 text-black">
                      Total Bet Amount for this Draw:
                    </h4>
                  </div>
                  <div className="col-md-4 text-md-end">
                    <h3 className="mb-0 text-primary fw-bold">
                      {formatCurrency(
                        betData.reduce((s, b) => s + (b.totalAmount || 0), 0),
                      )}
                    </h3>
                  </div>
                </div>
              </div>

              <div className="card-row flex-wrap">
                {numbers.map((n) => (
                  <div
                    key={n}
                    className="card-box"
                    style={{
                      cursor: loading ? "not-allowed" : "pointer",
                      opacity: loading ? 0.6 : 1,
                      // ── highlight green for 0 and 00 ──
                      border:
                        n === "0" || n === "00" ? "2px solid green" : undefined,
                    }}
                    onClick={() =>
                      !loading &&
                      handleDeclareResult(game, {
                        result: game === "roulette" ? n : Number(n),
                      })
                    }
                  >
                    <div className="result-circle">{n}</div>
                    <input
                      type="text"
                      className="card-input"
                      value={formatCurrency(betMap[n] || 0)}
                      readOnly
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center mt-5 text-muted">
            Select a draw to view bets
          </div>
        )}
      </div>
    );
  };

  // 🔒 PASSWORD GATE — page open pe ek baar
  if (!auth.ok) {
    return (
      <div
        style={{
          minHeight: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: 32,
            minWidth: 340,
            boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
          }}
        >
          <h5 className="mb-1 fw-bold">Manual Result — Access</h5>
          <p className="text-muted mb-3" style={{ fontSize: 13 }}>
            Enter the admin password to open this page.
          </p>

          <div className="mb-3">
            <label className="form-label fw-semibold">Password</label>
            <div className="input-group">
              <input
                type={auth.showPassword ? "text" : "password"}
                className={`form-control ${auth.error ? "is-invalid" : ""}`}
                placeholder="Enter password"
                value={auth.input}
                autoFocus
                onChange={(e) =>
                  setAuth((prev) => ({
                    ...prev,
                    input: e.target.value,
                    error: "",
                  }))
                }
                onKeyDown={(e) => e.key === "Enter" && handleAuthSubmit()}
              />
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() =>
                  setAuth((prev) => ({
                    ...prev,
                    showPassword: !prev.showPassword,
                  }))
                }
              >
                {auth.showPassword ? "🙈" : "👁️"}
              </button>
              {auth.error && (
                <div className="invalid-feedback d-block">{auth.error}</div>
              )}
            </div>
          </div>

          <button
            className="btn btn-danger fw-bold w-100"
            onClick={handleAuthSubmit}
          >
            Unlock
          </button>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="game-manual-result-page">
      <div className="container-fluid">
        <div className="page-hedding-header">
          <h1 className="page-heading h3 text-uppercase fs-4">Manual Result</h1>
        </div>

        <div className="row g-3">
          {/* RoyalKing */}
          <RoundsTable game="classic" title="RoyalKing" />
          <ClassicResultPanel />

          {/* Roulette */}
          <RoundsTable game="roulette" title="Roulette" />
          <NumberResultPanel game="roulette" />

          {/* Fun Target */}
          <RoundsTable game="funtarget" title="FUN Target" />
          <NumberResultPanel game="funtarget" />
        </div>
      </div>
    </div>
  );
};

export default GameManualResult;
